import { ClientEvent, KnownMembership, MatrixClient, SyncState } from 'matrix-js-sdk/src/matrix';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { listScheduledMeetings, MeetingType } from 'api/meedioBackend/listScheduledMeetings';
import { MeetingResponse } from 'api/meedioBackend/types';
import {
  isAccessibleVideoHubGuestMeeting,
  isVideoHubGuestMeeting,
} from 'modules/Notifications/meetingSpaceInviteUtils';
import logger from 'utils/logging/faro';

export interface BackendGuestMeetingRoomIdsResult {
  roomIds: ReadonlySet<string>;
  meetings: ReadonlyArray<MeetingResponse>;
  isLoaded: boolean;
}

const REFETCH_DEBOUNCE_MS = 5000;

const useBackendGuestMeetingRoomIds = (
  matrixClient: MatrixClient | undefined,
  joinedWorkspaces: Record<string, unknown>
): BackendGuestMeetingRoomIdsResult => {
  const [meetings, setMeetings] = useState<MeetingResponse[]>([]);
  const [allGuestMeetings, setAllGuestMeetings] = useState<MeetingResponse[]>([]);
  const [isLoaded, setIsLoaded] = useState(false);
  const fetchingRef = useRef(false);
  const lastFetchRef = useRef(0);

  const fetchGuestMeetings = useCallback(async () => {
    if (!matrixClient || fetchingRef.current) return;
    const myUserId = matrixClient.getUserId();
    if (!myUserId) return;

    if (isLoaded && Date.now() - lastFetchRef.current < REFETCH_DEBOUNCE_MS) return;

    fetchingRef.current = true;
    try {
      const allMeetings = await listScheduledMeetings({ type: MeetingType.Scheduled });
      const guestMeetings = allMeetings.filter((m) => isVideoHubGuestMeeting(m, myUserId));

      // #8314 — Rooms the user is actively invited to (or has joined). Used to grant access to
      // shared meetings tied to a workspace the user has not joined.
      const invitedMeetingRoomIds = new Set(
        matrixClient
          .getRooms()
          .filter((room) => {
            const membership = room.getMyMembership();
            return membership === KnownMembership.Invite || membership === KnownMembership.Join;
          })
          .map((room) => room.roomId)
      );

      const accessibleMeetings = guestMeetings.filter((m) =>
        isAccessibleVideoHubGuestMeeting(m, myUserId, joinedWorkspaces, invitedMeetingRoomIds)
      );
      setMeetings(accessibleMeetings);
      setAllGuestMeetings(guestMeetings);
      lastFetchRef.current = Date.now();
    } catch (error) {
      logger.warn('useBackendGuestMeetingRoomIds: fetch failed', error);
    } finally {
      setIsLoaded(true);
      fetchingRef.current = false;
    }
  }, [matrixClient, joinedWorkspaces, isLoaded]);

  useEffect(() => {
    if (!matrixClient) return;

    void fetchGuestMeetings();

    const handleSync = (state: SyncState) => {
      if (state === SyncState.Prepared || state === SyncState.Syncing) {
        void fetchGuestMeetings();
      }
    };
    const handleRoom = () => void fetchGuestMeetings();

    matrixClient.on(ClientEvent.Sync, handleSync);
    matrixClient.on(ClientEvent.Room, handleRoom);

    return () => {
      matrixClient.off(ClientEvent.Sync, handleSync);
      matrixClient.off(ClientEvent.Room, handleRoom);
    };
  }, [matrixClient, fetchGuestMeetings]);

  // Broad set — any guest meeting room ID, regardless of workspace join status.
  // Used by invitedSpaces / workspace hook to exclude meeting spaces from workspace toasts.
  const roomIds = useMemo(
    () => new Set(allGuestMeetings.map((m) => m.matrix_room_id).filter(Boolean) as string[]),
    [allGuestMeetings]
  );

  return { roomIds, meetings, isLoaded };
};

export default useBackendGuestMeetingRoomIds;
